// src/components/layout/Layout.js
import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Avatar, NotifDot } from '../ui';

const NAV_SECTIONS = [
  {
    label: 'Library', items: [
      { key: 'home', icon: '🏠', label: 'My Library', mobile: true },
      { key: 'search', icon: '🔍', label: 'Search', mobile: true },
      { key: 'compilations', icon: '📋', label: 'Compilations', mobile: false },
      { key: 'archive', icon: '📦', label: 'Archive', mobile: false },
    ]
  },
  {
    label: 'Research', items: [
      { key: 'notebooks', icon: '📝', label: 'My Notebooks', mobile: true },
      { key: 'bookmarks', icon: '🔖', label: 'Bookmarks', mobile: false },
    ]
  },
  {
    label: 'Tools', items: [
      { key: 'lexassist', icon: '✨', label: 'LexAssist AI', mobile: false, badge: 'AI' },
      { key: 'pricing', icon: '◉', label: 'Plans', mobile: false },
      { key: 'account', icon: '⚙️', label: 'Account', mobile: true },
    ]
  },
];

const ALL_NAV = NAV_SECTIONS.flatMap(s => s.items);

const Layout = ({ currentPage, onNav, children }) => {
  const { currentUser, notifications, sidebarOpen, setSidebarOpen } = useApp();
  const unread = notifications.filter(n => !n.read).length;
  const [notifOpen, setNotifOpen] = useState(false);
  const initials = (currentUser?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--page-bg)', fontFamily: 'Nunito,system-ui,sans-serif' }}>

      {/* ── Desktop Sidebar ── */}
      <aside className="hidden md:flex flex-col flex-shrink-0 bg-white border-r" style={{ width: 200, borderColor: 'var(--border)' }}>
        {/* Logo */}
        <div className="p-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="font-bold" style={{ fontFamily: 'Playfair Display,serif', fontSize: 17, color: 'var(--rose)' }}>AIR Reader</div>
          <div className="font-bold uppercase" style={{ fontSize: 9, color: 'var(--ink-lt)', letterSpacing: 2, marginTop: 2, fontFamily: 'Nunito,sans-serif' }}>Est. 1914 · Nagpur</div>
        </div>

        {/* Nav sections */}
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_SECTIONS.map(section => (
            <div key={section.label}>
              <div className="px-4 pt-3 pb-1 font-bold uppercase"
                style={{ fontSize: 9, color: 'var(--ink-faint)', letterSpacing: 2, fontFamily: 'Nunito,sans-serif' }}>
                {section.label}
              </div>
              {section.items.map(item => (
                <button key={item.key} onClick={() => onNav(item.key)}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-left transition-all"
                  style={{
                    fontSize: 12, fontWeight: 600, fontFamily: 'Nunito,sans-serif',
                    background: currentPage === item.key ? 'var(--rose-pale)' : 'transparent',
                    color: currentPage === item.key ? 'var(--rose)' : 'var(--ink-lt)',
                    borderRight: currentPage === item.key ? '2px solid var(--rose)' : '2px solid transparent',
                  }}>
                  <span style={{ fontSize: 14 }}>{item.icon}</span>
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded"
                      style={{ background: 'var(--lavender-pale)', color: 'var(--lavender)', fontFamily: 'Nunito,sans-serif' }}>
                      {item.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* User */}
        <div className="p-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <button onClick={() => onNav('account')} className="flex items-center gap-2.5 w-full hover:opacity-80 transition-opacity">
            <Avatar initials={initials} size="sm" />
            <div className="flex-1 min-w-0 text-left">
              <div className="truncate font-bold" style={{ fontSize: 11, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>
                {currentUser?.name || 'User'}
              </div>
              <div className="truncate" style={{ fontSize: 9, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>
                {currentUser?.plan || 'Free'}
              </div>
            </div>
          </button>
        </div>
      </aside>

      {/* ── Mobile Sidebar Overlay ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0" style={{ background: 'rgba(44,44,53,.4)' }} onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-lg animate-slide-right" style={{ overflow: 'auto' }}>
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
              <div>
                <div className="font-bold" style={{ fontFamily: 'Playfair Display,serif', fontSize: 17, color: 'var(--rose)' }}>AIR Reader</div>
                <div className="font-bold uppercase" style={{ fontSize: 9, color: 'var(--ink-lt)', letterSpacing: 2, fontFamily: 'Nunito,sans-serif' }}>Est. 1914</div>
              </div>
              <button onClick={() => setSidebarOpen(false)} style={{ color: 'var(--ink-lt)', fontSize: 18 }}>✕</button>
            </div>
            <nav className="py-2">
              {ALL_NAV.map(item => (
                <button key={item.key} onClick={() => { onNav(item.key); setSidebarOpen(false); }}
                  className="w-full flex items-center gap-3 px-5 py-3 transition-all"
                  style={{
                    fontSize: 13, fontWeight: 600, fontFamily: 'Nunito,sans-serif',
                    background: currentPage === item.key ? 'var(--rose-pale)' : 'transparent',
                    color: currentPage === item.key ? 'var(--rose)' : 'var(--ink-lt)',
                  }}>
                  <span style={{ fontSize: 16 }}>{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Mobile top bar — blush rose */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
          style={{ background: 'var(--rose-pale)', borderColor: 'var(--rose-lt)' }}>
          <button onClick={() => setSidebarOpen(true)} style={{ fontSize: 20, color: 'var(--ink)' }}>☰</button>
          <div className="font-bold" style={{ fontFamily: 'Playfair Display,serif', fontSize: 16, color: 'var(--rose)' }}>AIR Reader</div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button onClick={() => setNotifOpen(!notifOpen)} style={{ fontSize: 18, color: 'var(--ink-mid)' }}>🔔</button>
              <NotifDot count={unread} />
            </div>
            <Avatar initials={initials} size="sm" />
          </div>
        </header>

        {/* Desktop top bar */}
        <header className="hidden md:flex items-center justify-between px-6 py-3 bg-white border-b flex-shrink-0"
          style={{ borderColor: 'var(--border)' }}>
          {/* Search bar */}
          <button onClick={() => onNav('search')}
            className="flex items-center gap-2 rounded-lg px-4 py-2 border transition-all hover:border-rose-lt"
            style={{ background: 'var(--off-white)', borderColor: 'var(--border)', minWidth: 260 }}>
            <span style={{ color: 'var(--ink-faint)' }}>🔍</span>
            <span style={{ fontSize: 12, color: 'var(--ink-faint)', fontFamily: 'Nunito,sans-serif' }}>
              Search commentaries, bare acts, compilations…
            </span>
          </button>
          <div className="flex items-center gap-3">
            {/* Sync indicator */}
            <div className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 border text-[10px] font-bold font-ui"
              style={{ background: 'var(--sage-pale)', borderColor: '#C8E8D8', color: 'var(--sage)', fontFamily: 'Nunito,sans-serif' }}>
              🟢 Synced
            </div>
            <div className="relative">
              <button onClick={() => setNotifOpen(!notifOpen)}
                className="flex items-center justify-center rounded-lg border w-8 h-8 transition-colors hover:border-rose-lt"
                style={{ borderColor: 'var(--border)', background: 'var(--off-white)', fontSize: 15 }}>🔔</button>
              <NotifDot count={unread} />
            </div>
            <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => onNav('account')}>
              <span style={{ fontSize: 11, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>{currentUser?.name || 'User'}</span>
              <Avatar initials={initials} size="sm" />
            </div>
          </div>
        </header>

        {/* Notifications dropdown */}
        {notifOpen && (
          <div className="absolute right-4 top-14 z-50 w-80 bg-white rounded-xl shadow-lg border animate-scale-in"
            style={{ borderColor: 'var(--border)' }}>
            <div className="p-4 border-b font-bold text-sm font-ui" style={{ borderColor: 'var(--border)', color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>
              Notifications
            </div>
            {notifications.map(n => (
              <div key={n.id} className="p-4 border-b last:border-0"
                style={{ borderColor: 'var(--border-lt)', background: !n.read ? 'var(--rose-mist)' : 'white' }}>
                <div className="font-ui leading-relaxed" style={{ fontSize: 11, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>{n.text}</div>
                <div className="font-ui mt-1" style={{ fontSize: 10, color: 'var(--ink-faint)', fontFamily: 'Nunito,sans-serif' }}>{n.time}</div>
              </div>
            ))}
            <button onClick={() => setNotifOpen(false)} className="w-full text-center p-3 font-bold font-ui text-[11px]"
              style={{ color: 'var(--rose)', fontFamily: 'Nunito,sans-serif' }}>Close</button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">{children}</main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden flex border-t bg-white flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          {ALL_NAV.filter(i => i.mobile).map(item => (
            <button key={item.key} onClick={() => onNav(item.key)}
              className="flex-1 flex flex-col items-center py-2 gap-0.5 transition-colors"
              style={{ color: currentPage === item.key ? 'var(--rose)' : 'var(--ink-faint)' }}>
              <span style={{ fontSize: 18 }}>{item.icon}</span>
              <span className="font-bold font-ui" style={{ fontSize: 8, fontFamily: 'Nunito,sans-serif' }}>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default Layout;